import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { MenuRoute } from './menu';

// 懒加载页面
const DashboardPage = lazy(() => import('../pages/dashboard'));
const UserListPage = lazy(() => import('../pages/users/list'));
const UserFormPage = lazy(() => import('../pages/users/form'));

const EventEditionListPage = lazy(() => import('../pages/event-edition/list'));
const EventEditionFormPage = lazy(() => import('../pages/event-edition/form'));

const EventListPage = lazy(() => import('../pages/event/list'));
const EventFormPage = lazy(() => import('../pages/event/form'));
const EventDetailPage = lazy(() => import('../pages/event/detail'));

const VenueListPage = lazy(() => import('../pages/venue/list'));
const VenueFormPage = lazy(() => import('../pages/venue/form'));
const VenueSeatMapsPage = lazy(() => import('../pages/venue/seat-maps'));
const SeatMapDesignerPage = lazy(() => import('../pages/seat-map/designer'));

const AgendaManagementPage = lazy(() => import('../pages/agenda/management'));

const GuestListPage = lazy(() => import('../pages/guest/list'));
const GuestFormPage = lazy(() => import('../pages/guest/form'));
const GuestDetailPage = lazy(() => import('../pages/guest/detail'));
const GuestImportPage = lazy(() => import('../pages/guest/import'));

const CheckinManagementPage = lazy(() => import('../pages/checkin/management'));
const CheckinRecordsPage = lazy(() => import('../pages/checkin/records'));
const CheckinStatisticsPage = lazy(() => import('../pages/checkin/statistics'));

const NotificationTemplatesPage = lazy(() => import('../pages/notification/templates'));
const NotificationTemplateFormPage = lazy(() => import('../pages/notification/template-form'));
const NotificationRecordsPage = lazy(() => import('../pages/notification/records'));
const NotificationSendPage = lazy(() => import('../pages/notification/send'));

// Loading 组件
const PageLoading: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <span>加载中...</span>
  </div>
);

// 包装懒加载组件
const withSuspense = (Component: React.LazyExoticComponent<React.FC>): React.ReactNode => (
  <React.Suspense fallback={<PageLoading />}>
    <Component />
  </React.Suspense>
);

/**
 * 路由配置
 *
 * 定义管理端所有页面路由及其权限要求。
 * routes 数组用于在 App.tsx 中渲染 <Routes>。
 */
export interface AppRoute {
  path: string;
  element: React.ReactNode;
  menuKey?: string;
  roles?: string[];
}

export const routes: AppRoute[] = [
  // 工作台
  {
    path: '/dashboard',
    element: withSuspense(DashboardPage),
    menuKey: 'dashboard',
  },

  // 届次管理
  {
    path: '/event-editions',
    element: withSuspense(EventEditionListPage),
    menuKey: 'event-edition-list',
    roles: ['SUPER_ADMIN'],
  },
  {
    path: '/event-editions/create',
    element: withSuspense(EventEditionFormPage),
    menuKey: 'event-edition-list',
    roles: ['SUPER_ADMIN'],
  },
  {
    path: '/event-editions/:id/edit',
    element: withSuspense(EventEditionFormPage),
    menuKey: 'event-edition-list',
    roles: ['SUPER_ADMIN'],
  },
  {
    path: '/event-editions/:id',
    element: withSuspense(EventEditionFormPage),
    menuKey: 'event-edition-list',
    roles: ['SUPER_ADMIN'],
  },

  // 活动管理
  {
    path: '/events',
    element: withSuspense(EventListPage),
    menuKey: 'event-list',
  },
  {
    path: '/events/create',
    element: withSuspense(EventFormPage),
    menuKey: 'event-list',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/events/:id/edit',
    element: withSuspense(EventFormPage),
    menuKey: 'event-list',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/events/:id',
    element: withSuspense(EventDetailPage),
    menuKey: 'event-list',
  },

  // 场地管理
  {
    path: '/venues',
    element: withSuspense(VenueListPage),
    menuKey: 'venue-list',
  },
  {
    path: '/venues/create',
    element: withSuspense(VenueFormPage),
    menuKey: 'venue-list',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/venues/:id/edit',
    element: withSuspense(VenueFormPage),
    menuKey: 'venue-list',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/venues/:id',
    element: withSuspense(VenueFormPage),
    menuKey: 'venue-list',
  },
  {
    path: '/venues/:id/seat-maps',
    element: withSuspense(VenueSeatMapsPage),
    menuKey: 'venue-list',
  },
  {
    path: '/seat-maps/:id/designer',
    element: withSuspense(SeatMapDesignerPage),
    menuKey: 'venue-list',
  },

  // 议程管理
  {
    path: '/agenda',
    element: withSuspense(AgendaManagementPage),
    menuKey: 'agenda-management',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },

  // 嘉宾管理
  {
    path: '/guests',
    element: withSuspense(GuestListPage),
    menuKey: 'guest-list',
  },
  {
    path: '/guests/create',
    element: withSuspense(GuestFormPage),
    menuKey: 'guest-list',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/guests/import',
    element: withSuspense(GuestImportPage),
    menuKey: 'guest-import',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/guests/:id/edit',
    element: withSuspense(GuestFormPage),
    menuKey: 'guest-list',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/guests/:id',
    element: withSuspense(GuestDetailPage),
    menuKey: 'guest-list',
  },

  // 签到管理
  {
    path: '/checkin',
    element: withSuspense(CheckinManagementPage),
    menuKey: 'checkin-management',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/checkin/records',
    element: withSuspense(CheckinRecordsPage),
    menuKey: 'checkin-records',
  },
  {
    path: '/checkin/statistics',
    element: withSuspense(CheckinStatisticsPage),
    menuKey: 'checkin-statistics',
  },

  // 通知管理
  {
    path: '/notifications/templates',
    element: withSuspense(NotificationTemplatesPage),
    menuKey: 'notification-templates',
  },
  {
    path: '/notifications/templates/create',
    element: withSuspense(NotificationTemplateFormPage),
    menuKey: 'notification-templates',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/notifications/templates/:id/edit',
    element: withSuspense(NotificationTemplateFormPage),
    menuKey: 'notification-templates',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },
  {
    path: '/notifications/records',
    element: withSuspense(NotificationRecordsPage),
    menuKey: 'notification-records',
  },
  {
    path: '/notifications/send',
    element: withSuspense(NotificationSendPage),
    menuKey: 'notification-send',
    roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
  },

  // 用户管理
  {
    path: '/users',
    element: withSuspense(UserListPage),
    menuKey: 'user-list',
    roles: ['SUPER_ADMIN'],
  },
  {
    path: '/users/create',
    element: withSuspense(UserFormPage),
    menuKey: 'user-list',
    roles: ['SUPER_ADMIN'],
  },
  {
    path: '/users/:id/edit',
    element: withSuspense(UserFormPage),
    menuKey: 'user-list',
    roles: ['SUPER_ADMIN'],
  },
];

/**
 * 菜单路由配置（用于侧边栏菜单渲染）
 */
export const menuRoutes: MenuRoute[] = [
  {
    key: 'dashboard',
    path: '/dashboard',
    label: '工作台',
    icon: <span>📊</span>,
  },
  {
    key: 'event-edition-mgmt',
    label: '届次管理',
    icon: <span>🏆</span>,
    roles: ['SUPER_ADMIN'],
    children: [
      {
        key: 'event-edition-list',
        path: '/event-editions',
        label: '届次列表',
        roles: ['SUPER_ADMIN'],
      },
    ],
  },
  {
    key: 'event-mgmt',
    label: '活动管理',
    icon: <span>📅</span>,
    children: [
      {
        key: 'event-list',
        path: '/events',
        label: '活动列表',
      },
      {
        key: 'venue-list',
        path: '/venues',
        label: '场地管理',
      },
      {
        key: 'agenda-management',
        path: '/agenda',
        label: '议程管理',
        roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
      },
    ],
  },
  {
    key: 'guest-mgmt',
    label: '嘉宾管理',
    icon: <span>👥</span>,
    children: [
      {
        key: 'guest-list',
        path: '/guests',
        label: '嘉宾列表',
      },
      {
        key: 'guest-import',
        path: '/guests/import',
        label: '批量导入',
        roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
      },
    ],
  },
  {
    key: 'checkin-mgmt',
    label: '签到管理',
    icon: <span>✅</span>,
    children: [
      {
        key: 'checkin-management',
        path: '/checkin',
        label: '签到操作',
        roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
      },
      {
        key: 'checkin-records',
        path: '/checkin/records',
        label: '签到记录',
      },
      {
        key: 'checkin-statistics',
        path: '/checkin/statistics',
        label: '签到统计',
      },
    ],
  },
  {
    key: 'notification-mgmt',
    label: '消息通知',
    icon: <span>📨</span>,
    children: [
      {
        key: 'notification-templates',
        path: '/notifications/templates',
        label: '通知模板',
      },
      {
        key: 'notification-send',
        path: '/notifications/send',
        label: '发送通知',
        roles: ['SUPER_ADMIN', 'EVENT_ADMIN'],
      },
      {
        key: 'notification-records',
        path: '/notifications/records',
        label: '通知记录',
      },
    ],
  },
  {
    key: 'system',
    label: '系统管理',
    icon: <span>⚙️</span>,
    children: [
      {
        key: 'user-list',
        path: '/users',
        label: '用户管理',
        roles: ['SUPER_ADMIN'],
      },
    ],
  },
];
